import { useState } from 'react';
import McaSessionsPanel from '../../components/McaSessionsPanel';
import { showToastError, showToastSuccess } from '../../components/ToastNotifications';
import useSsoSessions, { revokeAllSsoSessions, revokeSsoSession } from '../../hooks/useSsoSessions';
import type { SsoRevokeScope, SsoSession } from '../../types/index';
import { startMetrcReauthSignIn } from '../../utils/metrcReauthSignIn';
import { getErrorMessage, getSafeEndpointMessage } from '../../utils/requestErrors';

// Profile page panel: the user's saved Metrc sign-ins. Renders nothing for users who never signed in with MCA.
export default function McaSessionsSection() {
  const { data, refetch } = useSsoSessions();
  const [busy, setBusy] = useState(false);

  if (!data?.isMcaUser) return null;

  const runRevoke = async (revoke: () => Promise<unknown>, successMessage: string, failureMessage: string) => {
    setBusy(true);
    try {
      await revoke();
      showToastSuccess(successMessage);
      await refetch();
    } catch (error) {
      console.error(error);
      showToastError(getSafeEndpointMessage(error, failureMessage));
    } finally {
      setBusy(false);
    }
  };

  const handleRevokeAll = () => runRevoke(
    revokeAllSsoSessions,
    'Your Metrc sign-ins were removed from RetailId.',
    'Could not remove your Metrc sign-ins.',
  );

  const handleRevokeSession = (scope: SsoRevokeScope) => runRevoke(
    () => revokeSsoSession(scope),
    scope.mcaFacilityId == null
      ? 'Your Metrc sign-in was removed from RetailId.'
      : 'Your Metrc sign-in for that facility was removed from RetailId.',
    'Could not remove your Metrc sign-in.',
  );

  // Redirects to Metrc; the callback saves the new tokens on these rows and returns here.
  const handleSignIn = async (session: SsoSession) => {
    setBusy(true);
    try {
      await startMetrcReauthSignIn({
        organizationId: session.organizationId,
        marketId: session.marketId,
        returnTo: '/profile',
      });
    } catch (error) {
      console.error(error);
      showToastError(getErrorMessage(error, 'Metrc sign-in could not be started.'));
      setBusy(false);
    }
  };

  return (
    <McaSessionsPanel
      sessions={data.sessions || []}
      busy={busy}
      onRevokeAll={handleRevokeAll}
      onRevokeSession={handleRevokeSession}
      onSignIn={handleSignIn}
    />
  );
}
